/**
 * SubconPayrollProvisioningService — one-click, first-time creation of the
 * four `iSyn2_SubconPayroll*` lists the SUBCONTRACTORS module needs, on the
 * Manufacturing site.
 *
 * Same safety posture as Isyn2ProvisioningService, whose idioms this reuses
 * wholesale: runs only on explicit user action, only creates lists that do
 * NOT exist, applies indexes at creation while the list is empty (the ~20k
 * index-refusal lesson), and `provision()` never adds a field to a list it
 * did not just create. `addMissingFields()` is the deliberate, visible
 * catch-up step for a column added after a list was first provisioned — the
 * "Migration Valve → Add missing columns" two-step this codebase runs on.
 *
 * FOUR LISTS, AND WHY NOT FEWER:
 *
 *   iSyn2_SubconPayrollProfiles  who to pay and into which account. Separate
 *       from the live `iSyn2_Subcontractors` registry on purpose — see
 *       payrollProfile.ts. Widening that list would put bank details behind
 *       the assignment board.
 *   iSyn2_SubconRateCard         what each band is worth. A list rather than
 *       a constant so a rate change is a data edit; the shipped card
 *       (rateCard.ts) seeds it and is the fallback when it is unreadable.
 *   iSyn2_SubconPayouts          the payslips. One row per payout, lines
 *       carried as JSON — see payout.ts for why the lines are not a list.
 *   iSyn2_SubconPayrollSettings  module-owned key/value, and today that means
 *       one thing: the letterhead logo every payslip prints, as a data URI.
 *       It is here rather than in `iSyn2_MfgSettings` because that list's
 *       value column is 255-character text — too small for an image by three
 *       orders of magnitude — and because a logo the payroll admin can swap
 *       from a screen is a data edit, not a deploy and a redeployed asset.
 *
 * NOTHING HERE TOUCHES A LIST THE REST OF THE APP READS. Payroll reads
 * iSyn2_QcScans, iSyn2_DispatchSlips and iSyn_WIPs and writes none of them.
 */
import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import { isListMissingError } from "./spErrors";
import { PAYOUT_STATUSES } from "../../modules/subcontractors/payout";
import { PAYMENT_MODES } from "../../modules/subcontractors/payrollProfile";

type FieldSpec =
  | { name: string; kind: "text"; indexed?: boolean }
  | { name: string; kind: "note" }
  | { name: string; kind: "number" }
  | { name: string; kind: "boolean" }
  | { name: string; kind: "datetime"; indexed?: boolean }
  | { name: string; kind: "choice"; choices: readonly string[] };

interface ListSpec {
  title: string;
  description: string;
  fields: FieldSpec[];
}

const KIND: Record<string, number> = { text: 2, note: 3, datetime: 4, choice: 6, boolean: 8, number: 9 };

export const SUBCON_PROFILES_LIST = "iSyn2_SubconPayrollProfiles";
export const SUBCON_RATECARD_LIST = "iSyn2_SubconRateCard";
export const SUBCON_PAYOUTS_LIST = "iSyn2_SubconPayouts";
export const SUBCON_SETTINGS_LIST = "iSyn2_SubconPayrollSettings";

export const SUBCON_PAYROLL_LIST_SPECS: ListSpec[] = [
  {
    title: SUBCON_PROFILES_LIST,
    description:
      "SUBCONTRACTORS · Payroll — paying facts per subcontractor (subcontractor ID, BIR side, bank account). " +
      "Joined to iSyn2_Subcontractors on ledgerKey; neither list writes the other.",
    fields: [
      { name: "ledgerKey", kind: "text", indexed: true },
      { name: "fullName", kind: "text" },
      { name: "contractorId", kind: "text", indexed: true },
      { name: "bir", kind: "choice", choices: ["BIR", "NON-BIR"] },
      { name: "companyName", kind: "text" },
      { name: "tin", kind: "text" },
      { name: "bankName", kind: "text" },
      // Text, never number: a leading zero is part of an account number.
      { name: "bankAccount", kind: "text" },
      { name: "modeOfPayment", kind: "choice", choices: PAYMENT_MODES },
      { name: "location", kind: "text" },
      { name: "section", kind: "text" },
      { name: "payoutSchedule", kind: "text" },
      { name: "dateStarted", kind: "datetime" },
      { name: "cellphone", kind: "text" },
      { name: "email", kind: "text" },
      { name: "position", kind: "text" },
      { name: "active", kind: "boolean" },
    ],
  },
  {
    title: SUBCON_RATECARD_LIST,
    description:
      "SUBCONTRACTORS · Payroll — the priced line items of the weekly payslip, seeded from the workbook's " +
      "PAYSLIP sheet rows 17-67. rateKey is the key; the workbook's short code repeats across groups.",
    fields: [
      { name: "rateKey", kind: "text", indexed: true },
      { name: "code", kind: "text" },
      // `group` is a reserved word in enough contexts to be worth avoiding.
      { name: "rateGroup", kind: "text" },
      { name: "item", kind: "text" },
      { name: "rate", kind: "number" },
      { name: "matchKind", kind: "text" },
      // JSON array of design codes. Note, not text: E7 alone carries eleven.
      { name: "designCodes", kind: "note" },
      { name: "sortOrder", kind: "number" },
      { name: "active", kind: "boolean" },
    ],
  },
  {
    title: SUBCON_PAYOUTS_LIST,
    description:
      "SUBCONTRACTORS · Payroll — one row per payslip, replacing RECORDS OF PAYOUT and NON-BIR RECORDS. " +
      "Work lines travel with the row as JSON. Amount columns are a snapshot for list readers; the app " +
      "always recomputes from the lines.",
    fields: [
      { name: "payoutRef", kind: "text", indexed: true },
      { name: "sewer", kind: "text", indexed: true },
      { name: "contractorId", kind: "text" },
      { name: "contractorName", kind: "text" },
      { name: "companyName", kind: "text" },
      { name: "tin", kind: "text" },
      { name: "bir", kind: "choice", choices: ["BIR", "NON-BIR"] },
      { name: "section", kind: "text" },
      { name: "periodStart", kind: "datetime" },
      { name: "periodEnd", kind: "datetime", indexed: true },
      { name: "dateIssued", kind: "datetime", indexed: true },
      { name: "modeOfPayment", kind: "text" },
      { name: "bankName", kind: "text" },
      { name: "bankAccount", kind: "text" },
      { name: "schedPayout", kind: "text" },
      { name: "linesJson", kind: "note" },
      { name: "additionsJson", kind: "note" },
      { name: "deductionsJson", kind: "note" },
      { name: "qtyTotal", kind: "number" },
      { name: "workTotal", kind: "number" },
      { name: "netAmount", kind: "number" },
      { name: "totalAmount", kind: "number" },
      { name: "payoutStatus", kind: "choice", choices: PAYOUT_STATUSES },
      { name: "remarks", kind: "note" },
      { name: "createdBy", kind: "text" },
      { name: "createdAt", kind: "datetime" },
      { name: "issuedBy", kind: "text" },
      { name: "issuedAt", kind: "datetime" },
      { name: "paidBy", kind: "text" },
      { name: "paidAt", kind: "datetime" },
      { name: "voidedBy", kind: "text" },
      { name: "voidedAt", kind: "datetime" },
      { name: "voidReason", kind: "note" },
    ],
  },
  {
    title: SUBCON_SETTINGS_LIST,
    description:
      "SUBCONTRACTORS · Payroll — module-owned settings, one row per key. settingText is a note column " +
      "because the payslip letterhead lives here as a data URI; iSyn2_MfgSettings' value column is 255 " +
      "characters of text and cannot hold one.",
    fields: [
      { name: "settingKey", kind: "text", indexed: true },
      // Note, not text: a downscaled letterhead is tens of thousands of
      // base64 characters. SharePoint's plain-text note column tops out at
      // 63,999, which is why the upload budgets itself well under that.
      { name: "settingText", kind: "note" },
      { name: "updatedBy", kind: "text" },
      { name: "updatedAt", kind: "datetime" },
    ],
  },
];

export class SubconPayrollProvisioningService {
  private readonly sp = createSharePointClient(MFG_SITE_URL);

  /** "present" / "absent" / "unknown" — a read that failed for some other
   *  reason must never be reported as a missing list, or the fix offered is
   *  "create it" when the list is sitting right there. */
  private async checkList(title: string): Promise<"present" | "absent" | "unknown"> {
    try {
      await this.sp.raw(`/_api/web/lists/getbytitle('${encodeURIComponent(title)}')?$select=Id`);
      return "present";
    } catch (e) {
      return isListMissingError(e) ? "absent" : "unknown";
    }
  }

  private async listExists(title: string): Promise<boolean> {
    return (await this.checkList(title)) === "present";
  }

  public async missingListsDetailed(): Promise<{ missing: string[]; unresolved: string[] }> {
    const missing: string[] = [];
    const unresolved: string[] = [];
    for (const spec of SUBCON_PAYROLL_LIST_SPECS) {
      const r = await this.checkList(spec.title);
      if (r === "absent") missing.push(spec.title);
      else if (r === "unknown") unresolved.push(spec.title);
    }
    return { missing, unresolved };
  }

  private async listFieldNames(listTitle: string): Promise<Set<string>> {
    const res = await this.sp.raw<{ d: { results: { InternalName: string }[] } }>(
      `/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/fields` +
      `?$select=InternalName&$filter=FromBaseType eq false and Hidden eq false`
    );
    return new Set(res.d.results.map(f => f.InternalName));
  }

  /** For every list that EXISTS, the spec fields it is short of. */
  public async missingFields(): Promise<{ list: string; fields: string[] }[]> {
    const out: { list: string; fields: string[] }[] = [];
    for (const spec of SUBCON_PAYROLL_LIST_SPECS) {
      if (!(await this.listExists(spec.title))) continue;
      const have = await this.listFieldNames(spec.title);
      const missing = spec.fields.map(f => f.name).filter(n => !have.has(n));
      if (missing.length > 0) out.push({ list: spec.title, fields: missing });
    }
    return out;
  }

  /** Adds ONLY the fields missing from an already-existing list. Never
   *  removes, never creates a list — the deliberate catch-up step. */
  public async addMissingFields(onProgress?: (m: string) => void): Promise<{ list: string; added: string[] }[]> {
    const gaps = await this.missingFields();
    const out: { list: string; added: string[] }[] = [];
    for (const { list, fields: names } of gaps) {
      const spec = SUBCON_PAYROLL_LIST_SPECS.find(s => s.title === list);
      if (!spec) continue;
      const added: string[] = [];
      for (const name of names) {
        const field = spec.fields.find(f => f.name === name);
        if (!field) continue;
        onProgress?.(`${list}: adding ${name}…`);
        await this.createField(list, field);
        added.push(name);
      }
      onProgress?.(`${list}: added ${added.join(", ")}`);
      out.push({ list, added });
    }
    return out;
  }

  /**
   * Creates every missing list. Each is provisioned independently so one
   * failure is logged and skipped rather than stranding the rest, and
   * SharePoint's own "already exists" is treated as success — the end state
   * provisioning wants is "the list exists", not "this call created it".
   */
  public async provision(onProgress?: (m: string) => void): Promise<string[]> {
    const created: string[] = [];
    const failed: string[] = [];
    for (const spec of SUBCON_PAYROLL_LIST_SPECS) {
      try {
        if (await this.listExists(spec.title)) {
          onProgress?.(`${spec.title} already exists — skipped`);
          continue;
        }
        await this.createList(spec, onProgress);
        created.push(spec.title);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes("already exists in this Web site")) {
          onProgress?.(`${spec.title} already exists (the check above missed it) — skipped`);
          continue;
        }
        onProgress?.(`✗ ${spec.title}: ${msg}`);
        failed.push(spec.title);
      }
    }
    if (failed.length > 0) onProgress?.(`${failed.length} list(s) need another look — re-run to retry: ${failed.join(", ")}`);
    return created;
  }

  private async createList(spec: ListSpec, onProgress?: (m: string) => void): Promise<void> {
    onProgress?.(`Creating ${spec.title}…`);
    await this.sp.raw(`/_api/web/lists`, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.List" },
        Title: spec.title,
        Description: spec.description,
        BaseTemplate: 100,
      }),
    });
    for (const field of spec.fields) await this.createField(spec.title, field);
    onProgress?.(`${spec.title} ready (${spec.fields.length} columns)`);
  }

  private async createField(listTitle: string, field: FieldSpec): Promise<void> {
    const fieldsUrl = `/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/fields`;

    if (field.kind === "choice") {
      await this.sp.raw(fieldsUrl, {
        method: "POST",
        body: JSON.stringify({
          __metadata: { type: "SP.FieldChoice" },
          Title: field.name,
          FieldTypeKind: KIND.choice,
          Required: false,
          Choices: { __metadata: { type: "Collection(Edm.String)" }, results: [...field.choices] },
        }),
      });
      return;
    }

    await this.sp.raw(fieldsUrl, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.Field" },
        Title: field.name,
        FieldTypeKind: KIND[field.kind],
        Required: false,
      }),
    });
    if ((field.kind === "text" || field.kind === "datetime") && field.indexed) {
      await this.setIndexed(listTitle, field.name);
    }
  }

  private async setIndexed(listTitle: string, fieldName: string): Promise<void> {
    await this.sp.raw(
      `/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')` +
      `/fields/getbyinternalnameortitle('${encodeURIComponent(fieldName)}')`,
      {
        method: "POST",
        headers: { "If-Match": "*", "X-HTTP-Method": "MERGE" },
        body: JSON.stringify({ __metadata: { type: "SP.Field" }, Indexed: true }),
      }
    );
  }
}

export const subconPayrollProvisioningService = new SubconPayrollProvisioningService();
